import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import { PaginatedResponse } from "@/shared/http";

import { CollectionRecord, CollectionResourceType } from "../CollectionRecord";

export interface UseCollectionsQueryOptions {
  orgId?: string;
  /** When set, restricts the result to collections holding this resource
   *  type (e.g. ``event`` collections for a create-events agent). */
  resourceType?: CollectionResourceType;
  limit?: number;
}

/**
 * Query key for {@link useCollectionsQuery}. Exported so callers can
 * invalidate or prefetch the collections list without re-spelling the array.
 */
export function getQueryKey(options?: UseCollectionsQueryOptions) {
  return [
    "collections",
    "list",
    options?.orgId,
    options?.resourceType,
    options?.limit,
  ];
}

/**
 * Org-scoped list of collections, newest first. Backs collection pickers
 * such as the agent-launch ``COLLECTION_ID`` variable input. Fetches a
 * single page (see ``CollectionService.searchCollections`` for the cap);
 * pickers fall back to free-text entry for ids beyond it.
 */
export function useCollectionsQuery(
  options?: UseCollectionsQueryOptions,
): UseQueryResult<PaginatedResponse<CollectionRecord>, Error> {
  const { collections } = useDomainServices();

  return useQuery<PaginatedResponse<CollectionRecord>, Error>({
    queryKey: getQueryKey(options),
    queryFn: ({ signal }) =>
      collections.searchCollections({
        orgId: options!.orgId!,
        resourceType: options?.resourceType,
        limit: options?.limit,
        abortSignal: signal,
      }),
    enabled: options?.orgId !== undefined,
    staleTime: 30_000,
  });
}
